import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Col,
  Button,
  Row,
  Typography,
  Layout,
  Checkbox,
  Card,
  TimePicker,
  Tabs,
  InputNumber,
  List,
  Tag,
  Empty,
  Skeleton,
  Space,
  Divider,
} from "antd";
import { useNavigate } from "react-router";
import { CloseCircleOutlined } from "@ant-design/icons";
import { Post } from "../../config/api/post";
import { Get } from "../../config/api/get";
import { MEETING, SCHEDULE, SESSION } from "../../config/constants/api";
import swal from "sweetalert";
import { FaLongArrowAltLeft } from "react-icons/fa";
import dayjs from "dayjs";

const getDefaultDays = () => [
  { day: "Sunday", selected: false, dayNo: 0, slots: [] },
  { day: "Monday", selected: false, dayNo: 1, slots: [] },
  { day: "Tuesday", selected: false, dayNo: 2, slots: [] },
  { day: "Wednesday", selected: false, dayNo: 3, slots: [] },
  { day: "Thursday", selected: false, dayNo: 4, slots: [] },
  { day: "Friday", selected: false, dayNo: 5, slots: [] },
  { day: "Saturday", selected: false, dayNo: 6, slots: [] },
];

function Meetings() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);

  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(getDefaultDays());
  const [selectedDate, setSelectedDate] = useState(0);
  const [slot, setSlot] = useState({ startTime: null, endTime: null });
  const [meetingCharge, setMeetingCharge] = useState(null);
  const [meetingChargeLoading, setMeetingChargeLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("availability");
  const [coachSessions, setCoachSessions] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [actionSessionId, setActionSessionId] = useState(null);

  useEffect(() => {
    getSchedule();
    getMeetingCharge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "bookings") {
      loadCoachSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const applyScheduleData = (data = []) => {
    const nextDays = getDefaultDays();
    data.forEach((responseObj) => {
      const day = nextDays.find((item) => item.dayNo === responseObj.day);
      if (day) {
        day.selected = true;
        day.slots = [...(responseObj.timeSlots || [])];
      }
    });
    setDays(nextDays);
  };

  const getSchedule = () => {
    try {
      Get(SCHEDULE.getMySchedule, token).then((response) => {
        if (response?.status) {
          applyScheduleData(response?.data || []);
        }
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const getMeetingCharge = () => {
    setMeetingChargeLoading(true);
    Get(MEETING.getMyMeetingCharge, token)
      .then((response) => {
        if (response?.status) {
          setMeetingCharge(response?.data?.meetingCharge?.charge ?? 0);
        } else {
          // Not set yet; keep this as first-time setup state.
          setMeetingCharge(0);
        }
      })
      .catch(() => {
        swal("Oops!", "Unable to load meeting charge", "error");
      })
      .finally(() => {
        setMeetingChargeLoading(false);
      });
  };

  const saveSchedule = () => {
    let hasError = false;
    const availability = days
      .filter((item) => item.selected)
      .map((item) => {
        if (item.slots.length === 0) {
          swal("Error!", "Please enter timeslots for all selected days", "error");
          hasError = true;
          return null;
        }
        return { day: item.dayNo, timeSlots: item.slots };
      })
      .filter(Boolean);

    if (hasError) {
      return;
    }

    setLoading(true);
    const data = {
      coachId: user._id,
      availability,
    };

    Post(SCHEDULE.addSchedule, data, token, null)
      .then((response) => {
        if (response?.data?.status) {
          swal("Success!", "Schedule updated successfully", "success");
          setSlot({ startTime: null, endTime: null });
          getSchedule();
        } else {
          swal("Oops!", response?.data?.message, "error");
        }
      })
      .catch(() => {
        swal("Oops!", "Unable to save schedule", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChecked = (checked, indx) => {
    setDays((prev) =>
      prev.map((day, index) =>
        index === indx ? { ...day, selected: checked } : day
      )
    );
  };

  const addSlot = () => {
    if (!slot.startTime || !slot.endTime) {
      swal("Error!", "Select start and end time", "error");
      return;
    }
    if (slot.startTime > slot.endTime) {
      swal("Error!", "End time must be greater than start time", "error");
      return;
    }
    setDays((prev) =>
      prev.map((day) => {
        if (day.dayNo !== selectedDate) return day;
        return { ...day, slots: [...day.slots, slot] };
      })
    );
    setSlot({ startTime: null, endTime: null });
  };

  const removeSlot = (indx) => {
    setDays((prev) =>
      prev.map((day) => {
        if (day.dayNo !== selectedDate) return day;
        return {
          ...day,
          slots: day.slots.filter((_, index) => index !== indx),
        };
      })
    );
  };

  const loadCoachSessions = () => {
    setBookingsLoading(true);
    Get(SESSION.coach, token)
      .then((response) => {
        if (response?.status) {
          setCoachSessions(response?.data?.sessions || []);
        } else {
          setCoachSessions([]);
        }
      })
      .catch(() => setCoachSessions([]))
      .finally(() => setBookingsLoading(false));
  };

  const formatSlotLabel = (slot) => {
    const start = dayjs(slot.startTime);
    const end = dayjs(slot.endTime);
    if (!start.isValid() || !end.isValid()) return "—";
    return `${start.format("hh:mm A")} - ${end.format("hh:mm A")}`;
  };

  const approveSession = (sessionId) => {
    setActionSessionId(sessionId);
    Post(`${SESSION.approve}${sessionId}/approve`, {}, token, null)
      .then((response) => {
        const api = response?.data || {};
        if (api?.status) {
          swal("Success!", api?.message || "Session approved", "success");
          loadCoachSessions();
        } else {
          swal("Oops!", api?.message || "Could not approve", "error");
        }
      })
      .catch(() => swal("Oops!", "Could not approve session", "error"))
      .finally(() => setActionSessionId(null));
  };

  const declineSession = (sessionId) => {
    setActionSessionId(sessionId);
    Post(`${SESSION.decline}${sessionId}/decline`, {}, token, null)
      .then((response) => {
        const api = response?.data || {};
        if (api?.status) {
          swal("Success!", api?.message || "Session declined", "success");
          loadCoachSessions();
        } else {
          swal("Oops!", api?.message || "Could not decline", "error");
        }
      })
      .catch(() => swal("Oops!", "Could not decline session", "error"))
      .finally(() => setActionSessionId(null));
  };

  const isUpcomingSessionDay = (date) =>
    dayjs(date).startOf("day").valueOf() >= dayjs().startOf("day").valueOf();

  const pendingSessions = coachSessions.filter((s) => s.status === "PENDING");
  const approvedSessions = coachSessions.filter((s) => s.status === "APPROVED");
  const upcomingPaidSessions = coachSessions.filter(
    (s) => s.status === "PAID" && isUpcomingSessionDay(s.date)
  );

  const startMeeting = (sessionId) => {
    setActionSessionId(sessionId);
    Post(`${SESSION.startMeeting}${sessionId}`, {}, token, null)
      .then((response) => {
        const api = response?.data || {};
        if (api?.status && api?.data?.startUrl) {
          window.open(api.data.startUrl, "_blank", "noopener,noreferrer");
          swal(
            "Success!",
            api?.message || "Zoom host view opened in a new tab",
            "success"
          );
          loadCoachSessions();
        } else {
          swal("Oops!", api?.message || "Could not start meeting", "error");
        }
      })
      .catch(() => swal("Oops!", "Could not start meeting", "error"))
      .finally(() => setActionSessionId(null));
  };

  const renderSessionList = (items, showActions) => (
    <List
      dataSource={items}
      locale={{ emptyText: <Empty description="No bookings in this section" /> }}
      renderItem={(item) => (
        <List.Item
          key={item._id}
          actions={
            showActions
              ? [
                  <Button
                    key="approve"
                    type="primary"
                    size="small"
                    loading={actionSessionId === item._id}
                    onClick={() => approveSession(item._id)}
                  >
                    Approve
                  </Button>,
                  <Button
                    key="decline"
                    danger
                    size="small"
                    disabled={actionSessionId === item._id}
                    onClick={() => declineSession(item._id)}
                  >
                    Decline
                  </Button>,
                ]
              : [
                  <Tag key="status" color="blue">
                    Awaiting payment
                  </Tag>,
                ]
          }
        >
          <List.Item.Meta
            title={
              <Space wrap>
                <span className="fontFamily1" style={{ textTransform: "capitalize" }}>
                  {item.student?.firstName} {item.student?.lastName}
                </span>
                <Tag>{item.status}</Tag>
              </Space>
            }
            description={
              <div className="fontFamily1">
                <div>{dayjs(item.date).format("MMM D, YYYY")}</div>
                <div style={{ marginTop: 4 }}>
                  {(item.slots || []).map((s, i) => (
                    <Tag key={i}>{formatSlotLabel(s)}</Tag>
                  ))}
                </div>
                <div style={{ marginTop: 4 }}>Total: ${item.charges}</div>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );

  const renderUpcomingPaidList = (items) => (
    <List
      dataSource={items}
      locale={{ emptyText: <Empty description="No upcoming paid sessions" /> }}
      renderItem={(item) => (
        <List.Item
          key={item._id}
          actions={[
            <Button
              key="start"
              type="primary"
              className="loginButton"
              size="small"
              loading={actionSessionId === item._id}
              onClick={() => startMeeting(item._id)}
            >
              {item.meetingStartedAt ? "Open Zoom again" : "Start meeting"}
            </Button>,
          ]}
        >
          <List.Item.Meta
            title={
              <Space wrap>
                <span className="fontFamily1" style={{ textTransform: "capitalize" }}>
                  {item.student?.firstName} {item.student?.lastName}
                </span>
                <Tag color="green">PAID</Tag>
                {item.meetingStartedAt ? (
                  <Tag color="processing">Started</Tag>
                ) : null}
              </Space>
            }
            description={
              <div className="fontFamily1">
                <div>{dayjs(item.date).format("MMM D, YYYY")}</div>
                <div style={{ marginTop: 4 }}>
                  {(item.slots || []).map((s, i) => (
                    <Tag key={i}>{formatSlotLabel(s)}</Tag>
                  ))}
                </div>
                <div style={{ marginTop: 4, color: "#6b7280" }}>
                  Opens Zoom in a new browser tab. Session stays here until the end of the day.
                </div>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );

  const bookingsTab = (
    <Card className="infoBox" bordered={false}>
      <Typography.Title level={4} className="fontFamily1">
        Bookings
      </Typography.Title>
      <Typography.Paragraph className="fontFamily1" style={{ color: "#555" }}>
        Approve requests, then start paid meetings in Zoom when it is time. Paid sessions remain
        listed until the end of the session day so you can re-open Zoom if needed.
      </Typography.Paragraph>
      {bookingsLoading ? (
        <Skeleton active />
      ) : (
        <>
          <Typography.Title level={5} className="fontFamily1">
            Upcoming meetings (paid)
          </Typography.Title>
          {renderUpcomingPaidList(upcomingPaidSessions)}
          <Divider />
          <Typography.Title level={5} className="fontFamily1">
            Pending approval
          </Typography.Title>
          {renderSessionList(pendingSessions, true)}
          <Divider />
          <Typography.Title level={5} className="fontFamily1">
            Approved (awaiting payment)
          </Typography.Title>
          {renderSessionList(approvedSessions, false)}
        </>
      )}
    </Card>
  );

  const saveMeetingCharge = () => {
    const charge = Number(meetingCharge);
    if (Number.isNaN(charge) || charge < 0) {
      swal("Error!", "Charge must be 0 or greater", "error");
      return;
    }

    setMeetingChargeLoading(true);
    Post(MEETING.setMeetingCharge, { charge }, token, null)
      .then((response) => {
        if (response?.data?.status) {
          swal("Success!", "Meeting charge saved successfully", "success");
          setMeetingCharge(response?.data?.data?.meetingCharge?.charge ?? charge);
        } else {
          swal("Oops!", response?.data?.message, "error");
        }
      })
      .catch(() => {
        swal("Oops!", "Unable to save meeting charge", "error");
      })
      .finally(() => {
        setMeetingChargeLoading(false);
      });
  };

  const availabilityTab = (
    <Card className="infoBox" bordered={false} style={{ width: "100%", padding: "20px" }}>
      <Typography.Title className="fontFamily1" style={{ fontSize: "18px", fontWeight: 600, color: "black", textAlign: "left", marginTop: 0, marginBottom: 20 }}>
        Availability
      </Typography.Title>
      <Row className="flex" style={{ border: "1px solid #dadada", borderRadius: "10px", overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            width: "100%",
            overflowX: "auto",
            scrollbarWidth: "thin",
          }}
        >
          {days.map((item, index) => (
            <div
              key={item.dayNo}
              onClick={() => setSelectedDate(item.dayNo)}
              style={{
                minWidth: "130px",
                height: "60px",
                cursor: "pointer",
                backgroundColor: item.dayNo === selectedDate ? "#243d61" : "white",
                borderRight: index < 6 && "1px solid #dadada",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Checkbox checked={item.selected} onChange={(e) => handleChecked(e.target.checked, index)}>
                <Typography.Title className="fontFamily1" style={{ fontSize: "12px", fontWeight: 600, color: item.dayNo === selectedDate ? "white" : "black", textAlign: "left", marginTop: 0, marginBottom: 0 }}>
                  {item.day}
                </Typography.Title>
              </Checkbox>
            </div>
          ))}
        </div>
      </Row>

      <br />
      <br />

      <Typography.Title className="fontFamily1" style={{ fontSize: "18px", fontWeight: 600, color: "black", textAlign: "left", marginTop: 0, marginBottom: 20 }}>
        Slots
      </Typography.Title>
      {days[selectedDate]?.slots.length === 0 && (
        <Typography.Text className="fontFamily1" style={{ fontSize: "14px", color: "black", textAlign: "left", marginTop: 0, marginBottom: 20 }}>
          No Slots Added
        </Typography.Text>
      )}

      <Row>
        {days[selectedDate].slots.length > 0 &&
          days[selectedDate].slots.map((subItem, subIndex) => (
            <Col xs={24} md={6} key={`${selectedDate}-${subIndex}`}>
              <Typography.Text className="fontFamily1" style={{ fontSize: "16px", color: "black", textAlign: "left", marginTop: 0, marginBottom: 20 }}>
                <CloseCircleOutlined
                  onClick={() => removeSlot(subIndex)}
                  style={{ color: "#B00000", fontSize: "16px", marginTop: 5 }}
                />
                &emsp;
                {dayjs(new Date(subItem.startTime)).format("hh:mm A") +
                  " to " +
                  dayjs(new Date(subItem.endTime)).format("hh:mm A")}
              </Typography.Text>
            </Col>
          ))}
      </Row>

      <br />
      <br />
      <Typography.Title className="fontFamily1" style={{ fontSize: "18px", fontWeight: 600, color: "black", textAlign: "left", marginTop: 0, marginBottom: 20 }}>
        Add Slots
      </Typography.Title>

      <Row gutter={10}>
        <Col xs={12} md={4}>
          <TimePicker
            size="large"
            style={{ width: "100%" }}
            placeholder="Start Time"
            value={slot.startTime}
            onChange={(e) => setSlot({ ...slot, startTime: e })}
            format={"hh:mm:A"}
          />
        </Col>
        <Col xs={12} md={4}>
          <TimePicker
            size="large"
            style={{ width: "100%" }}
            placeholder="End Time"
            value={slot.endTime}
            onChange={(e) => setSlot({ ...slot, endTime: e })}
            format={"hh:mm:A"}
          />
        </Col>
        <Col xs={12} md={5}>
          <Button style={{ backgroundColor: "#243d61", color: "white" }} size="large" onClick={addSlot}>
            Add
          </Button>
        </Col>
      </Row>
      <br />

      <Row justify="center">
        <Button type="primary" className="loginButton" onClick={saveSchedule}>
          {loading ? "Loading..." : "Save Schedule"}
        </Button>
      </Row>
    </Card>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Row
        className="scheduleBackground"
        style={{
          height: "45vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography.Title className="fontFamily1" style={{ fontSize: "30px", color: "#333d47" }}>
          Meetings
        </Typography.Title>

        <FaLongArrowAltLeft
          style={{
            fontSize: "30px",
            color: "#333d47",
            position: "absolute",
            left: 150,
            cursor: "pointer",
            marginTop: 20,
          }}
          onClick={() => navigate(-1)}
        />
      </Row>

      <Row className="whiteBackground" style={{ backgroundColor: "white", justifyContent: "center" }}>
        <Col xs={24} md={18}>
          <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", padding: "50px 20px" }}>
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
              }}
              destroyInactiveTabPane={false}
              items={[
                {
                  key: "availability",
                  label: "Availability",
                  children: availabilityTab,
                },
                {
                  key: "charges",
                  label: "Meeting Charges",
                  children: (
                    <Card className="infoBox" bordered={false}>
                      <Typography.Title level={4} className="fontFamily1">
                        Meeting Charges
                      </Typography.Title>
                      <Typography.Paragraph className="fontFamily1" style={{ color: "#555" }}>
                        Set the per-slot booking charge students will pay for your meetings.
                      </Typography.Paragraph>
                      <Row gutter={12} align="middle">
                        <Col xs={24} md={10}>
                          <InputNumber
                            min={0}
                            style={{ width: "100%" }}
                            value={meetingCharge}
                            onChange={(value) => setMeetingCharge(value)}
                            addonBefore="$"
                            placeholder="Enter charge"
                            size="large"
                          />
                        </Col>
                        <Col xs={24} md={8}>
                          <Button
                            type="primary"
                            className="loginButton"
                            loading={meetingChargeLoading}
                            onClick={saveMeetingCharge}
                          >
                            Save Charge
                          </Button>
                        </Col>
                      </Row>
                    </Card>
                  ),
                },
                {
                  key: "bookings",
                  label: "Bookings",
                  children: bookingsTab,
                },
              ]}
            />
          </div>
        </Col>
      </Row>
    </Layout>
  );
}

export default Meetings;
